import React from "react";
import { Phone, Mail, MapPin, Copy, Link, Edit, UserMinus } from "lucide-react";
import { Contact } from "@/bryntum-lib/models/Contact";
import { Button } from "@/modern/components/ui/button";
import { UserAvatar } from "@/modern/components/user-avatar";
import { useEntityContactContext } from "@/react/components/entityViews/contacts/components/context/EntityContactContext";
import { MoreMenu, MenuItem } from "@/modern/components/more-menu";
import { toast } from "sonner";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";

interface ContactCardProps {
  contact: Contact;
}

export const ContactCard: React.FC<ContactCardProps> = ({ contact }) => {
  const { canEdit, handleUnassignContact, copyContactToClipboard, copyContactLink, entityRecord } = useEntityContactContext();
  const { editorHandler } = useSwivuBryntumContext();

  const handleOpenClick = () => {
    editorHandler.openEditor(
      EDITOR_KEYS.contactEditor,
      { id: contact.id },
      {
        contact,
        entityRecord
      },
      { by: "recordId", useExisting: true }
    );
  };

  const handleCopyClick = () => {
    copyContactToClipboard(contact);
  };

  const handleDeleteClick = () => {
    handleUnassignContact(contact);
  };

  const menuItems: MenuItem[] = [
    {
      itemVariant: "button",
      menuText: "Open",
      Component: ({ onFinish }) => (
        <div
          className="flex items-center w-full cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenClick();
            onFinish?.();
          }}
        >
          <Edit className="h-4 w-4 mr-2" />
          <span>Open</span>
        </div>
      )
    },
    {
      itemVariant: "button",
      menuText: "Copy Contact",
      Component: ({ onFinish }) => (
        <div
          className="flex items-center w-full cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleCopyClick();
            onFinish?.();
          }}
        >
          <Copy className="h-4 w-4 mr-2" />
          <span>Copy Contact</span>
        </div>
      )
    },
    {
      itemVariant: "button",
      menuText: "Copy Link",
      Component: ({ onFinish }) => (
        <div
          className="flex items-center w-full cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            copyContactLink(contact);
            onFinish?.();
          }}
        >
          <Link className="h-4 w-4 mr-2" />
          <span>Copy Link</span>
        </div>
      )
    },
    ...(canEdit
      ? [
          { itemVariant: "separator" as const },
          {
            itemVariant: "button" as const,
            menuText: "Unassign",
            dropdownMenuItemProps: { className: "text-destructive" },
            Component: ({ onFinish }: { onFinish?: () => void }) => (
              <div
                className="flex items-center w-full cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                  onFinish?.();
                }}
              >
                <UserMinus className="h-4 w-4 mr-2" />
                <span>Unassign</span>
              </div>
            )
          }
        ]
      : [])
  ];

  return (
    <>
      <div className="group relative flex items-start gap-4 p-4  mx-2 transition-colors border-b">
        {/* Avatar */}
        <UserAvatar
          user={{
            id: contact.id as number,
            name: contact?.name || "N/A",
            profileImageURL: undefined,
            archived: false
          }}
          size="default"
          avatarClassName="h-10 w-10"
        />

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Row 1: Name + Menu */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium truncate">{contact.name || "N/A"}</h3>
            <MoreMenu menuItems={menuItems} triggerButtonClassName="-mt-2 -mr-2" />
          </div>

          {/* Row 2: Email */}
          <div className="group/email flex items-center gap-1.5 text-xs">
            <Mail className="h-3 w-3 shrink-0 text-blue-500 dark:text-blue-400" />
            <span className="truncate text-foreground/80">{contact.email || "N/A"}</span>
            {contact.email && (
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 opacity-0 group-hover/email:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(contact.email);
                  toast.success("Email copied");
                }}
              >
                <Copy className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Row 3: Phone */}
          <div className="group/phone flex items-center gap-1.5 text-xs">
            <Phone className="h-3 w-3 shrink-0 text-green-500 dark:text-green-400" />
            <span className="truncate text-foreground/80">{contact.phone || "N/A"}</span>
            {contact.phone && (
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 opacity-0 group-hover/phone:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(contact.phone);
                  toast.success("Phone copied");
                }}
              >
                <Copy className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Row 4: Address */}
          <div className="flex items-center gap-1.5 text-xs">
            <MapPin className="h-3 w-3 shrink-0 text-amber-500 dark:text-amber-400" />
            <span className="truncate text-foreground/80">{contact.address || "N/A"}</span>
          </div>
        </div>
      </div>
    </>
  );
};
